import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Mail } from 'lucide-react';
import { PRODUCTS, COMPANY_INFO } from '../mock';
import { useCart } from '../context/CartContext';

const ThankYou = () => {
  const [params] = useSearchParams();
  const fromCart = params.get('from') === 'cart' || params.get('from') === 'checkout';
  const fromCheckout = params.get('from') === 'checkout';
  const orderQueryNumber = params.get('order') || '';
  const customerEmail = params.get('email') || '';
  const isPaid = params.get('paid') === '1';
  const cadCharged = params.get('cad') || '';
  const buyNowSlug = params.get('product');
  const buyNowOption = params.get('option') || '';

  const { items, subtotal, clear } = useCart();

  const buyNowProduct = useMemo(
    () => (buyNowSlug ? PRODUCTS.find((p) => p.slug === buyNowSlug) : null),
    [buyNowSlug]
  );

  // Use real Fortune order number from checkout flow if present, fallback to placeholder
  const orderNumber = useMemo(() => {
    if (orderQueryNumber) return `FAD-${orderQueryNumber}`;
    return `FAD-${Date.now().toString().slice(-6)}`;
  }, [orderQueryNumber]);

  // If this thank-you came from the cart drawer/checkout, clear it after rendering
  useEffect(() => {
    if (fromCart && items.length > 0) {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCart]);

  // Build a display list: either Buy Now single product, sessionStorage
  // snapshot (post-checkout, cart cleared), or current cart items.
  const displayItems = useMemo(() => {
    if (buyNowProduct) {
      const v = (buyNowProduct.variants || []).find((vv) => vv.option === buyNowOption);
      const price = v?.price ?? buyNowProduct.price;
      return [{
        productId: buyNowProduct.id,
        slug: buyNowProduct.slug,
        name: buyNowProduct.name,
        price,
        image: buyNowProduct.images?.[0],
        variantOption: buyNowOption,
        quantity: 1,
      }];
    }
    if (items.length > 0) return items;
    // Cart already cleared (post-checkout) — restore from sessionStorage
    if (fromCheckout) {
      try {
        const raw = sessionStorage.getItem('lastOrderItems');
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (_) { /* ignore */ }
    }
    return [];
  }, [buyNowProduct, buyNowOption, items, fromCheckout]);

  const orderTotal = displayItems.reduce((n, i) => n + i.price * i.quantity, 0);

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Hero */}
      <section className="pt-40 pb-12 bg-neutral-50 border-b border-neutral-100">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <CheckCircle2 className="text-[#FF6B00] mx-auto mb-5" size={56} strokeWidth={1.6} />
          <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-3">
            {isPaid ? 'PAYMENT CONFIRMED' : (fromCheckout ? 'ORDER REQUEST RECEIVED' : 'ORDER CONFIRMED')}
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4">
            {isPaid ? 'THANK YOU — PAYMENT RECEIVED' : (fromCheckout ? 'THANKS — WE\u2019VE GOT YOUR REQUEST' : 'THANK YOU FOR YOUR ORDER')}
          </h1>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            {isPaid ? (
              <>
                Your payment was received{cadCharged ? <> (charged <strong className="text-neutral-900">CA${Number(cadCharged).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>)</> : ''} and a receipt is on its way to <strong className="text-neutral-900">{customerEmail || 'your inbox'}</strong>. Our build team will confirm fitment and finish details shortly.
              </>
            ) : fromCheckout ? (
              <>
                We&rsquo;ve received your order request and emailed a confirmation to <strong className="text-neutral-900">{customerEmail || 'your inbox'}</strong>. Our team will review availability and follow up shortly with a secure invoice and payment details.
              </>
            ) : (
              <>We&rsquo;ve received your order and a confirmation email is on its way. Our build team will reach out within 1 business day to confirm fitment and finish details.</>
            )}
          </p>
          <div
            data-testid="order-number"
            className="mt-8 inline-block border border-neutral-200 px-6 py-3 bg-white"
          >
            <div className="font-display tracking-[0.22em] text-[10px] text-neutral-500">
              ORDER NUMBER
            </div>
            <div className="font-display text-2xl tracking-tight">{orderNumber}</div>
          </div>
        </div>
      </section>

      {/* Payment confirmation callout */}
      {isPaid ? (
        <section className="py-10 bg-neutral-50 border-y border-neutral-200" data-testid="paid-callout">
          <div className="max-w-[680px] mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF6B00] mb-5">
              <CheckCircle2 size={26} strokeWidth={1.6} />
            </div>
            <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-2">
              PAYMENT SUCCESSFUL
            </div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-3 text-neutral-900">
              YOUR CARD HAS BEEN CHARGED{cadCharged ? ` CA$${Number(cadCharged).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
            </h2>
            <p className="text-neutral-600 text-sm leading-relaxed max-w-md mx-auto">
              A receipt was emailed to{' '}
              <strong className="text-neutral-900">{customerEmail || 'your inbox'}</strong>. Prices are listed in USD; your card was charged the CAD equivalent. Reply to the email anytime with questions.
            </p>
          </div>
        </section>
      ) : fromCheckout && (
        <section className="py-10 bg-neutral-50 border-y border-neutral-200" data-testid="request-received-callout">
          <div className="max-w-[680px] mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF6B00] mb-5">
              <Mail size={26} strokeWidth={1.6} />
            </div>
            <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-2">
              CHECK YOUR INBOX
            </div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-3 text-neutral-900">
              REQUEST RECEIVED — WE&rsquo;LL BE IN TOUCH
            </h2>
            <p className="text-neutral-600 text-sm leading-relaxed max-w-md mx-auto">
              We just emailed a confirmation of your request to{' '}
              {customerEmail ? (
                <strong className="text-neutral-900">{customerEmail}</strong>
              ) : (
                <strong className="text-neutral-900">your inbox</strong>
              )}.
              Our team will review availability and send you a secure invoice with payment details shortly. No payment is needed right now.
            </p>
            <p className="text-xs text-neutral-500 mt-5">
              Don&rsquo;t see it within a few minutes? Check your spam folder, or email{' '}
              <a href="mailto:info@fortuneautodesign.com" className="text-[#FF6B00] underline underline-offset-2">info@fortuneautodesign.com</a>{' '}
              and we&rsquo;ll resend.
            </p>
          </div>
        </section>
      )}

      {/* Summary */}
      <section className="py-14">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <h2 className="font-display text-xl tracking-[0.18em] mb-5">ORDER SUMMARY</h2>

          {displayItems.length === 0 ? (
            <p className="text-neutral-500 text-sm">No items found in this order.</p>
          ) : (
            <div className="border border-neutral-200">
              <ul className="divide-y divide-neutral-200">
                {displayItems.map((it, idx) => (
                  <li
                    key={`${it.productId}-${idx}`}
                    className="flex items-center gap-4 px-5 py-4"
                    data-testid={`order-line-${it.productId}`}
                  >
                    <div className="w-20 h-20 bg-neutral-50 border border-neutral-100 flex-shrink-0 overflow-hidden">
                      {it.image && (
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${it.slug}`}
                        className="font-display text-base tracking-wide hover:text-[#FF6B00] transition-colors block"
                      >
                        {it.name}
                      </Link>
                      {it.variantOption && (
                        <div className="text-xs text-neutral-500 mt-0.5 font-display tracking-[0.15em]">
                          {it.variantOption.toUpperCase()}
                        </div>
                      )}
                      <div className="text-xs text-neutral-500 mt-1">Qty {it.quantity}</div>
                    </div>
                    <div className="font-display text-base tabular-nums">
                      ${(it.price * it.quantity).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-5 py-4 bg-neutral-50 flex items-baseline justify-between border-t border-neutral-200">
                <span className="font-display tracking-[0.22em] text-[11px] text-neutral-500">
                  TOTAL
                </span>
                <span className="font-display text-2xl tabular-nums">
                  ${orderTotal.toLocaleString()}{' '}
                  <span className="text-xs text-neutral-500 align-middle">USD</span>
                </span>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(isPaid ? [
              { n: '01', title: 'PAYMENT CONFIRMED', body: 'Your card payment was received and a receipt emailed to you.' },
              { n: '02', title: 'BUILD CONSULTATION', body: 'Our team reaches out to confirm spec, fitment and finish details.' },
              { n: '03', title: 'PRODUCTION', body: 'Your build begins. Lead times vary by product — contact us for specifics.' },
              { n: '04', title: 'SHIPPING INVOICE', body: 'When your build is packed, we send a separate shipping invoice with carrier + tracking.' },
            ] : fromCheckout ? [
              { n: '01', title: 'REQUEST RECEIVED', body: 'We\u2019ve got your order request and emailed you a confirmation.' },
              { n: '02', title: 'AVAILABILITY & INVOICE', body: 'Our team confirms availability and emails you a secure invoice with payment details.' },
              { n: '03', title: 'PAYMENT', body: 'Pay your invoice securely, one-on-one with our team — whichever method suits you.' },
              { n: '04', title: 'PRODUCTION & SHIPPING', body: 'Your build begins, and we send a separate shipping invoice with carrier + tracking when it\u2019s ready.' },
            ] : [
              { n: '01', title: 'CONFIRMATION EMAIL', body: 'Check your inbox for order details and a PDF receipt.' },
              { n: '02', title: 'BUILD CONSULTATION', body: 'Our team contacts you within 1 business day to confirm spec.' },
              { n: '03', title: 'PRODUCTION', body: 'Your part is built to spec. Fulfillment timelines vary by product — please contact us for specific lead times.' },
              { n: '04', title: 'SHIPPING INVOICE', body: 'Once your order is packed, we send a separate shipping invoice with carrier, tracking, and final cost.' },
            ]).map((s) => (
              <div key={s.n} className="border border-neutral-200 p-5">
                <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-2">
                  STEP {s.n}
                </div>
                <h3 className="font-display text-sm tracking-[0.15em] mb-2">{s.title}</h3>
                <p className="text-xs text-neutral-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Shipping policy callout */}
          <div
            data-testid="thankyou-shipping-notice"
            className="mt-8 bg-[#FFF7ED] border-l-4 border-[#FF6B00] px-6 py-5"
          >
            <div className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] font-semibold mb-2">
              ABOUT YOUR SHIPPING CHARGE
            </div>
            <p className="text-sm text-neutral-800 leading-relaxed">
              {fromCheckout && !isPaid ? (
                <>Your invoice will cover your <strong>product only</strong>. Carbon fiber parts vary widely in size and weight, so we don&rsquo;t lock in a shipping number up front &mdash; that&rsquo;s how customers end up overpaying. Once your build is packed, we send a separate invoice with the actual carrier, dimensions, tracking number, and final shipping cost. One click, secure payment, and your part is on the way.</>
              ) : (
                <>The amount you paid today is for your <strong>product only</strong>. Carbon fiber parts vary widely in size and weight, so we don&rsquo;t lock in a shipping number at checkout &mdash; that&rsquo;s how customers end up overpaying. Instead, once your order is built and packed, we&rsquo;ll send a separate invoice with the actual carrier, dimensions, tracking number, and final shipping cost. One click, secure payment, and your part is on the way.</>
              )}
            </p>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              data-testid="thankyou-back-home"
              className="bg-black hover:bg-neutral-900 text-white px-7 py-3.5 font-display tracking-[0.22em] text-xs font-bold inline-flex items-center gap-2 transition-colors"
            >
              CONTINUE SHOPPING <ArrowRight size={14} />
            </Link>
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="border-2 border-neutral-300 hover:border-neutral-700 text-neutral-700 px-7 py-3 font-display tracking-[0.22em] text-xs font-bold inline-flex items-center gap-2 transition-colors"
            >
              <Mail size={14} /> CONTACT SUPPORT
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
